import React, { useState, useContext } from 'react';
import { 
  Container, Typography, Box, Button, Dialog, 
  DialogContent, DialogActions, Table, TableBody, TableCell, 
  TableHead, TableRow, IconButton, Chip, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, Grid, TextField
} from '@mui/material';
import { StyledTableContainer, tableRowHoverStyles, iconButtonStyles, StyledDialogTitle } from '../components/StyledComponents';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useQuery, useMutation, gql } from '@apollo/client';
import UserForm from '../components/UserForm';
import AuthContext from '../contexts/AuthContext';
import SearchableSelect from '../components/SearchableSelect';

// GraphQL queries et mutations
const GET_USERS = gql`
  query GetUsers($role: String, $departmentId: ID) {
    users(role: $role, departmentId: $departmentId) {
      id
      email
      username
      firstName
      lastName
      role
      department {
        id
        name
      }
      function {
        id
        name
      }
      phoneNumber
      otpEnabled
    }
  }
`;

const GET_DEPARTMENTS = gql`
  query GetDepartments {
    departments {
      id
      name
    }
  }
`;


const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    deleteUser(id: $id) {
      success
      message
    }
  }
`;

const RESET_USER_PASSWORD = gql`
  mutation ResetUserPassword($id: ID!, $password: String!) {
    updateUser(id: $id, password: $password) {
      success
      message
      user {
        id
      }
    }
  }
`;

const SEND_PASSWORD_RESET_LINK = gql`
  mutation SendPasswordResetLink($userId: ID!) {
    sendPasswordResetLink(userId: $userId) {
      success
      message
    }
  }
`;

function Users() {
  const { user } = useContext(AuthContext);
  
  // États pour les dialogues
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState(false);
  
  // État pour l'utilisateur sélectionné
  const [selectedUser, setSelectedUser] = useState(null);
  
  // État pour les messages d'erreur
  const [errorMessage, setErrorMessage] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [resetPasswordValues, setResetPasswordValues] = useState({
    password: '',
    confirmPassword: ''
  });
  const [resetLinkMessage, setResetLinkMessage] = useState('');

  // État pour le filtre de rôle
  const [roleFilter, setRoleFilter] = useState('');

  // État pour le filtre de département
  const [departmentFilter, setDepartmentFilter] = useState('');

  // État pour la recherche
  const [searchQuery, setSearchQuery] = useState('');
  
  // Récupérer la liste des utilisateurs
  const { data: usersData, loading: usersLoading, error: usersError, refetch: refetchUsers } = useQuery(GET_USERS, {
    variables: { role: roleFilter, departmentId: departmentFilter || null },
    skip: !['ADMIN', 'MANAGER', 'RH'].includes(user?.role)
  });

  // Récupérer les utilisateurs du département du gestionnaire
  const { data: departmentUsersData, loading: departmentUsersLoading, refetch: refetchDepartmentUsers } = useQuery(GET_USERS, {
    variables: { departmentId: user?.department?.id },
    skip: !['ADMIN', 'MANAGER', 'RH'].includes(user?.role)
  });

  // Récupérer la liste des départements
  const { data: departmentsData } = useQuery(GET_DEPARTMENTS);

  // Combiner et filtrer les utilisateurs selon les permissions
  const users = React.useMemo(() => {
    if (!['ADMIN', 'MANAGER', 'RH'].includes(user?.role)) {
      return [];
    }

    const allUsers = usersData?.users || [];
    const departmentUsers = departmentUsersData?.users || [];
    
    // Pour les RH et admins, montrer tous les utilisateurs
    if (user?.role === 'ADMIN' || user?.role === 'RH') {
      return allUsers;
    }
    
    // Pour les managers, montrer seulement les utilisateurs de leur département
    return departmentUsers;
  }, [usersData, departmentUsersData, user?.role]);

  // Filtrer les utilisateurs en fonction du rôle, du département et de la recherche
  const filteredUsers = React.useMemo(() => {
    let filtered = users;

    // Exclure les visiteurs de la liste des utilisateurs
    filtered = filtered.filter(user => user.role !== 'VISITOR');

    // Filtrer par rôle
    if (roleFilter) {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Filtrer par département
    if (departmentFilter) {
      filtered = filtered.filter(user => user.department?.id === departmentFilter);
    }

    // Filtrer par recherche
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user => 
        user.firstName.toLowerCase().includes(query) ||
        user.lastName.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [users, roleFilter, departmentFilter, searchQuery]);

  const loading = usersLoading || departmentUsersLoading;
  const error = usersError || departmentUsersData?.error;

  // Fonction pour rafraîchir les données
  const refetch = React.useCallback(() => {
    refetchUsers();
    refetchDepartmentUsers();
  }, [refetchUsers, refetchDepartmentUsers]);
  
  // Mutation pour supprimer un utilisateur
  const [deleteUser, { loading: deleteLoading }] = useMutation(DELETE_USER, {
    onCompleted: (data) => {
      if (data.deleteUser.success) {
        setOpenDeleteDialog(false);
        setSelectedUser(null);
        setErrorMessage('');
        refetch();
      } else {
        setErrorMessage(data.deleteUser.message || "Une erreur est survenue lors de la suppression");
      }
    },
    onError: (error) => {
      console.error("Erreur lors de la suppression:", error);
      setErrorMessage(`Erreur: ${error.message}`);
    }
  });

  const [sendPasswordResetLink, { loading: sendResetLinkLoading }] = useMutation(SEND_PASSWORD_RESET_LINK, {
    onCompleted: (data) => {
      if (data.sendPasswordResetLink.success) {
        setResetLinkMessage(data.sendPasswordResetLink.message);
        setResetPasswordError('');
      } else {
        setResetPasswordError(data.sendPasswordResetLink.message);
        setResetLinkMessage('');
      }
    },
    onError: (error) => {
      setResetPasswordError(`Erreur: ${error.message}`);
      setResetLinkMessage('');
    }
  });

  const [resetUserPassword, { loading: resetPasswordLoading }] = useMutation(RESET_USER_PASSWORD, {
    onCompleted: (data) => {
      if (data.updateUser.success) {
        setOpenResetPasswordDialog(false);
        setResetPasswordError('');
        setResetPasswordValues({ password: '', confirmPassword: '' });
        setSelectedUser(null);
      } else {
        setResetPasswordError(data.updateUser.message || "Erreur lors de la réinitialisation du mot de passe");
      }
    },
    onError: (error) => {
      setResetPasswordError(`Erreur: ${error.message}`);
    }
  });
  
  // Gérer l'ouverture du dialogue de création
  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
  };
  
  // Gérer la fermeture du dialogue de création
  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };
  
  // Gérer l'ouverture du dialogue de modification
  const handleOpenEditDialog = (user) => {
    setSelectedUser(user);
    setOpenEditDialog(true);
  };
  
  // Gérer la fermeture du dialogue de modification
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedUser(null);
  };
  
  // Gérer l'ouverture du dialogue de suppression
  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };
  
  // Gérer la fermeture du dialogue de suppression
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedUser(null);
  };

  const handleOpenResetPasswordDialog = (user) => {
    setSelectedUser(user);
    setResetPasswordValues({ password: '', confirmPassword: '' });
    setResetPasswordError('');
    setResetLinkMessage('');
    setOpenResetPasswordDialog(true);
  };

  const handleCloseResetPasswordDialog = () => {
    setOpenResetPasswordDialog(false);
    setResetPasswordValues({ password: '', confirmPassword: '' });
    setResetPasswordError('');
    setResetLinkMessage('');
    setSelectedUser(null);
  };

  const handleSendResetLink = () => {
    if (!selectedUser) return;
    setResetLinkMessage('');
    setResetPasswordError('');
    sendPasswordResetLink({ variables: { userId: selectedUser.id } });
  };
  
  // Gérer la suppression d'un utilisateur
  const handleDeleteUser = () => {
    if (selectedUser) {
      deleteUser({
        variables: {
          id: selectedUser.id
        }
      });
    }
  };

  const handleResetPasswordSubmit = () => {
    if (!selectedUser) return;

    if (!resetPasswordValues.password) {
      setResetPasswordError('Le nouveau mot de passe est requis');
      return;
    }

    if (resetPasswordValues.password.length < 8) {
      setResetPasswordError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (resetPasswordValues.password !== resetPasswordValues.confirmPassword) {
      setResetPasswordError('Les mots de passe doivent correspondre');
      return;
    }

    resetUserPassword({
      variables: {
        id: selectedUser.id,
        password: resetPasswordValues.password
      }
    });
  };
  
  // Gérer le succès de la création d'un utilisateur
  const handleCreateSuccess = () => {
    setOpenCreateDialog(false);
    refetch();
  };
  
  // Gérer le succès de la modification d'un utilisateur
  const handleEditSuccess = () => {
    setOpenEditDialog(false);
    setSelectedUser(null);
    refetch();
  };
  
  // Afficher un indicateur de chargement si les données sont en cours de chargement
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  // Afficher un message d'erreur si une erreur s'est produite
  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">
            Erreur lors du chargement des utilisateurs: {error.message}
          </Alert>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Gestion des Utilisateurs
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Type d'utilisateur</InputLabel>
              <Select
                value={roleFilter}
                label="Type d'utilisateur"
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="ADMIN">Administrateur</MenuItem>
                <MenuItem value="MANAGER">Gestionnaire</MenuItem>
                <MenuItem value="ACCEUIL">Accueil</MenuItem>
                <MenuItem value="RH">Ressources Humaines</MenuItem>
                <MenuItem value="EMPLOYEE">Employé</MenuItem>
                <MenuItem value="HOST">Hôte</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SearchableSelect
              label="Département"
              value={departmentFilter}
              onChange={(value) => setDepartmentFilter(value)}
              options={[
                { id: '', name: 'Tous' },
                ...(departmentsData?.departments || [])
              ]}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              noOptionsText="Aucun département disponible"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Rechercher"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              placeholder="Rechercher par nom, prénom ou email..."
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
              fullWidth
            >
              Utilisateur
            </Button>
          </Grid>
        </Grid>
        
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rôle</TableCell>
                <TableCell>Département</TableCell>
                <TableCell>Fonction</TableCell>
                <TableCell>Téléphone</TableCell>
                <TableCell>2FA</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user, index) => (
                <TableRow 
                  key={user.id}
                  sx={{
                    backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                    ...tableRowHoverStyles
                  }}
                >
                  <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip 
                      label={
                        user.role === 'ADMIN' ? 'Administrateur' :
                        user.role === 'MANAGER' ? 'Gestionnaire' :
                        user.role === 'ACCEUIL' ? 'Accueil' :
                        user.role === 'RH' ? 'Ressources Humaines' :
                        user.role === 'EMPLOYEE' ? 'Employé' :
                        user.role === 'HOST' ? 'Hôte' : 'Visiteur'
                      }
                      color={
                        user.role === 'ADMIN' ? 'error' :
                        user.role === 'MANAGER' ? 'warning' :
                        user.role === 'ACCEUIL' ? 'warning' :
                        user.role === 'RH' ? 'secondary' :
                        user.role === 'EMPLOYEE' ? 'primary' :
                        user.role === 'HOST' ? 'info' : 'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{user.department?.name || '-'}</TableCell>
                  <TableCell>{user.function?.name || '-'}</TableCell>
                  <TableCell>{user.phoneNumber || '-'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={user.otpEnabled ? 'Activé' : 'Désactivé'}
                      color={user.otpEnabled ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', '& > *': { display: 'inline-block', marginRight: '4px' } }}>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenEditDialog(user)}
                      sx={iconButtonStyles}
                      size="small"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="secondary"
                      onClick={() => handleOpenResetPasswordDialog(user)}
                      sx={iconButtonStyles}
                      size="small"
                      title="Réinitialiser le mot de passe"
                    >
                      <LockResetIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleOpenDeleteDialog(user)}
                      sx={iconButtonStyles}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </Box>
      
      {/* Dialogue de création d'utilisateur */}
      <Dialog 
        open={openCreateDialog} 
        onClose={handleCloseCreateDialog}
        maxWidth="md"
        fullWidth
      >
        <StyledDialogTitle>Créer un nouvel utilisateur</StyledDialogTitle>
        <DialogContent>
          <UserForm onSuccess={handleCreateSuccess} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Annuler</Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue de modification d'utilisateur */}
      <Dialog 
        open={openEditDialog} 
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
      >
        <StyledDialogTitle>Modifier l'utilisateur</StyledDialogTitle>
        <DialogContent>
          <UserForm 
            onSuccess={handleEditSuccess} 
            initialData={selectedUser}
            isEdit={true}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Annuler</Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue de suppression d'utilisateur */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={handleCloseDeleteDialog}
      >
        <StyledDialogTitle>Confirmer la suppression</StyledDialogTitle>
        <DialogContent>
          {selectedUser && (
            <Typography>
              Êtes-vous sûr de vouloir supprimer l'utilisateur {selectedUser.firstName} {selectedUser.lastName} ?
            </Typography>
          )}
          {errorMessage && (
            <Typography color="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annuler</Button>
          <Button 
            onClick={handleDeleteUser} 
            color="error"
            disabled={deleteLoading || !selectedUser}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openResetPasswordDialog}
        onClose={handleCloseResetPasswordDialog}
        maxWidth="sm"
        fullWidth
      >
        <StyledDialogTitle>Réinitialiser le mot de passe</StyledDialogTitle>
        <DialogContent>
          {selectedUser && (
            <Typography sx={{ mb: 2 }}>
              Utilisateur: {selectedUser.firstName} {selectedUser.lastName}
            </Typography>
          )}
          {resetPasswordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resetPasswordError}
            </Alert>
          )}
          {resetLinkMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {resetLinkMessage}
            </Alert>
          )}

          {/* Option 1 : envoyer un lien de réinitialisation par email */}
          <Alert severity="info" sx={{ mb: 1 }}>
            Envoyez un lien sécurisé par email : l'utilisateur choisira lui-même son nouveau mot de passe.
          </Alert>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<LockResetIcon />}
            onClick={handleSendResetLink}
            disabled={sendResetLinkLoading || !selectedUser}
            sx={{ mb: 2 }}
          >
            {sendResetLinkLoading ? <CircularProgress size={22} /> : 'Envoyer un lien de réinitialisation par email'}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Ou définissez manuellement un nouveau mot de passe :
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            label="Nouveau mot de passe"
            type="password"
            value={resetPasswordValues.password}
            onChange={(e) => setResetPasswordValues((prev) => ({ ...prev, password: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Confirmer le mot de passe"
            type="password"
            value={resetPasswordValues.confirmPassword}
            onChange={(e) => setResetPasswordValues((prev) => ({ ...prev, confirmPassword: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetPasswordDialog}>Annuler</Button>
          <Button
            onClick={handleResetPasswordSubmit}
            variant="contained"
            disabled={resetPasswordLoading || !selectedUser}
          >
            {resetPasswordLoading ? <CircularProgress size={24} /> : 'Réinitialiser'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Users; 
